import Accordion from "@/components/Accordion";
import { tv } from "tailwind-variants";
import { Caption } from "@/components/Typography";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightFromLine,
  faClipboardListCheck,
  faPersonWalkingArrowLoopLeft,
  faShoppingCart,
  faXmarkToSlot,
} from "@fortawesome/pro-duotone-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

const styles = tv({
  slots: {
    gridRow: "grid w-full grid-cols-4 grid-rows-1 gap-30",
    gridRowFill: "col-span-3",
    caption: "font-semibold uppercase !leading-[25px]",
    statRow: "flex flex-row justify-between gap-10 sm:items-center",
    statText:
      "text-[22px] font-bold leading-[35px] text-primary sm:text-[30px] sm:leading-[50px]",
    statTextIcon: "h-25 text-gray-dark",
    subGrid:
      "grid-rows-auto grid grid-cols-1 gap-20 sm:grid-cols-4 sm:grid-rows-1 sm:gap-30",
    subStatText: "text-[21px] font-bold leading-[35px]",
    subStat: "flex flex-col items-start gap-10",
    subStatIcon: "h-20 text-gray-dark",
    statLabel: "font-semibold text-gray-dark",
  },
  variants: {
    marginBottom: {
      true: {
        gridRow: "mb-30",
      },
    },
    hideOnMobile: {
      true: {
        gridRowFill: "hidden sm:block",
      },
    },
    isHeader: {
      true: {
        gridRow: "[&>*:first-child]:col-span-4 sm:[&>*:first-child]:col-span-1",
      },
    },
    isStat: {
      true: {
        gridRow: "grid-cols-2 sm:grid-cols-4",
        gridRowFill: "col-span-1 sm:col-span-3",
      },
    },
  },
});

interface IProps {
  data: IGetUsageDashboardRS;
}

const DataRelationships = ({ data }: IProps) => {
  const { t } = useTranslation();
  const {
    gridRow,
    gridRowFill,
    caption,
    statRow,
    statText,
    statTextIcon,
    statLabel,
    subGrid,
    subStat,
    subStatIcon,
    subStatText,
  } = styles();

  const {
    numFirstTimeVerifiedContacts,
    numVerifiedContactUpdates,
    numReturningCheckouts,
    numUnsubscribes,
    numDoubleOptIns,
  } = data;

  const renderTitle = () => (
    <div className={gridRow({ isHeader: true })}>
      <Caption className={caption()}>
        {t("email.usage.dataRelationships.titleLeft")}
      </Caption>
      <div className={gridRowFill({ hideOnMobile: true })}>
        <Caption className={caption()}>
          {t("email.usage.dataRelationships.titleRight")}
        </Caption>
      </div>
    </div>
  );

  const renderLeftStat = ({
    value,
    label,
  }: {
    value: number;
    label: string;
  }) => (
    <div className={statRow()}>
      <div>
        <p className={statText()} data-testid="usage-dashboard-stat">
          {value.toLocaleString()}
        </p>
        <Caption className={statLabel()}>{label}</Caption>
      </div>
      <FontAwesomeIcon
        fixedWidth
        icon={faArrowRightFromLine}
        className={statTextIcon()}
      />
    </div>
  );

  const renderIndividualStat = ({
    icon,
    value,
    label,
  }: {
    icon: IconProp;
    value: number;
    label: string;
  }) => (
    <div className={subStat()}>
      <FontAwesomeIcon icon={icon} className={subStatIcon()} />
      <p className={subStatText()} data-testid="usage-dashboard-stat">
        {value.toLocaleString()}
      </p>
      <Caption className={statLabel()}>{label}</Caption>
    </div>
  );

  const renderBody = () => (
    <div>
      <div className={gridRow({ marginBottom: true, isStat: true })}>
        {renderLeftStat({
          value: numFirstTimeVerifiedContacts,
          label: t("email.usage.dataRelationships.stats.verifiedContacts"),
        })}
        <div className={gridRowFill({ isStat: true })}>
          <div className={subGrid()}>
            {renderIndividualStat({
              icon: faShoppingCart,
              value: numFirstTimeVerifiedContacts,
              label: t(
                "email.usage.dataRelationships.stats.firstTimePurchases",
              ),
            })}
          </div>
        </div>
      </div>
      <div className={gridRow({ isStat: true })}>
        {renderLeftStat({
          value: numVerifiedContactUpdates,
          label: t("email.usage.dataRelationships.stats.updates"),
        })}
        <div className={gridRowFill({ isStat: true })}>
          <div className={subGrid()}>
            {renderIndividualStat({
              icon: faPersonWalkingArrowLoopLeft,
              value: numReturningCheckouts,
              label: t("email.usage.dataRelationships.stats.repeatPurchases"),
            })}
            {renderIndividualStat({
              icon: faXmarkToSlot,
              value: numUnsubscribes,
              label: t("email.usage.dataRelationships.stats.unsubscribes"),
            })}
            {renderIndividualStat({
              icon: faClipboardListCheck,
              value: numDoubleOptIns,
              label: t("email.usage.dataRelationships.stats.doubleOptIns"),
            })}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Accordion
      topContent={renderTitle()}
      hasShadow={false}
      defaultIsExpanded
      hideToggle>
      {renderBody()}
    </Accordion>
  );
};

export default DataRelationships;
